import xss from 'xss';
import { DEFAULT_LOCALE } from './locale';

/**
 * Replaces {{variable}} in a string
 *
 * @param input The string to interpolate
 * @param data The variables to replace, replaces {{key}} with the value of data[key]
 */
export const interpolateStringWithData = (
  input: string,
  data: Record<string, string | number>
) => {
  const dataStringified = Object.entries(data).reduce((previous, current) => {
    const [key, value] = current;
    if (typeof value === 'number') {
      return {
        ...previous,
        [key]: value.toString(),
      };
    }
    return {
      ...previous,
      [key]: value,
    };
  }, {});

  return input.replace(/\{\{(\w+)\}\}/g, (match, variable) => {
    return dataStringified[variable] || match;
  });
};

/**
 * Replaces [text](link) patterns with HTML <a> tags, ensuring safety from script tags and javascript: URLs.
 * Escapes any HTML tags in the input to prevent XSS attacks.
 *
 * @param input The string containing potential [text](link) patterns
 * @returns The string with links replaced by <a href="...">text</a>, and HTML tags escaped
 */
export const replaceLinks = (input: string): string => {
  // Escape the entire input to prevent XSS attacks
  let escapedInput = xss(input);

  // Only quote-free http(s) and root-relative URLs match, so no other scheme and no attribute breakout.
  escapedInput = escapedInput.replace(
    /\[([^\[\]]+)\]\((https?:\/\/[^\s)"]+|\/[^\s)"]+)\)/g,
    (_, text, url) => `<a href="${url}">${text}</a>`
  );

  return escapedInput;
};

/**
 * Converts a name into up to 2 initials.
 * If one word passed in, returns first letter.
 * If two words, returns first letter of each word.
 * If multiple words, returns first letter of the first and last words.
 *
 * @param name
 * @returns up to 2 initials of the passed in name
 */
export const toAvatarInitials = (name: string) => {
  const names = name.trim().split(' ');
  const firstInitial = names[0].slice(0, 1).toUpperCase();
  const lastInitial =
    names.length > 1 ? names[names.length - 1].slice(0, 1).toUpperCase() : '';
  return `${firstInitial}${lastInitial}`;
};

/**
 * Inserts an underscore between a lower case and uppercase letter and converts the string to uppercase
 */
export const toUpperSnakeCase = (input: string) => {
  return input.replace(/([a-z])([A-Z])/g, '$1_$2').toUpperCase();
};

/**
 * Parses the input as an absolute URL on the `http:` or `https:` scheme.
 *
 * Returns `null` for values that are not absolute URLs (`example.com`,
 * `//example.com`, `website`, empty strings), for absolute URLs on other schemes
 * (`javascript:`, `data:`, `mailto:`, `ftp:`) and for URLs carrying credentials
 * (`https://unity.com@evil.com` reads like one host but navigates to another).
 * Malformed-but-parseable hosts such as `http://.foo.co,uk` are returned: they
 * still resolve off-site.
 *
 * Anything that renders the value must render `url.href`, never the raw input.
 * The parser accepts same-scheme-relative spellings such as `https:example.com`
 * and normalises them to `https://example.com/`, but a browser given that raw
 * string as an `href` on an https page resolves it relative to the current page.
 *
 * @param input the candidate URL
 * @returns the parsed URL, or `null` when it is not an absolute http(s) URL
 */
export const parseAbsoluteHttpUrl = (input?: string): URL | null => {
  if (!input) {
    return null;
  }

  let url: URL;

  try {
    url = new URL(input);
  } catch (_) {
    return null;
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return null;
  }

  if (url.username || url.password) {
    return null;
  }

  return url;
};

/**
 * Checks whether the input parses as an absolute http(s) URL. Same contract as
 * {@link parseAbsoluteHttpUrl}; prefer that one when the value will be rendered,
 * so the normalised `href` is what reaches the DOM.
 *
 * @param input the candidate URL
 * @returns true when the input is an absolute http(s) URL
 */
export const isAbsoluteHttpUrl = (input?: string): input is string =>
  parseAbsoluteHttpUrl(input) !== null;

export const stringToBoolean = (input: string): boolean => {
  return input === 'true';
};

/**
 * A function to be used to pluralize texts coming from the CMS.
 * @param key The property name in the entry object WITHOUT the form suffix. e.g. 'subtotal' instead of 'subtotalOne'
 * @param entry The object containing the pluralized text fields
 * @param count The number to determine the plural form
 * @param locale The locale to use for pluralization, uses the default locale if not provided
 * @param ordinal Whether to use ordinal pluralization. e.g. '1st', '2nd', '3rd', '4th'
 * @returns the pluralized text with count interpolated
 */
export function pluralize(
  key: string,
  entry: object,
  count: number,
  locale: string = DEFAULT_LOCALE,
  ordinal: boolean = false
) {
  const pr = new Intl.PluralRules(locale, {
    type: ordinal ? 'ordinal' : 'cardinal',
  });
  // e.g. 'one', 'other'
  const form = pr.select(count);
  const text = buildText(key, entry, form, count);

  if (fallbackText(text, locale)) {
    return pluralize(key, entry, count, DEFAULT_LOCALE, ordinal);
  }

  return text;
}

function buildText(key: string, entry: object, form: string, count: number) {
  const text = entry[key + capitalizeFirstLetter(form)];
  return text.replace('{{count}}', count);
}

function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

/**
 * If the text is not in the default locale,
 * then we check if the text is ASCII only.
 */
function fallbackText(text: string, locale: string) {
  return locale !== DEFAULT_LOCALE && /^[\u0000-\u007f]*$/.test(text);
}

export const extractDomainFromEmail = (email: string) => {
  if (!email.includes('@')) {
    return '';
  }

  const splitEmail = email.split('@');
  return splitEmail[splitEmail.length - 1];
};

/**
 * Replaces spaces with underscore and removes all special characters so titles can be used as IDs.
 * Keeps underscores as they are used to separate words.
 */
export const formatTitleForID = (title: string) => {
  return title && typeof title === 'string'
    ? title.replaceAll(' ', '_').replace(/[^a-zA-Z0-9_]/g, '')
    : '';
};

/**
 * Truncates text at a given length and adds a (configurable) elipsis after the text that has been truncated.
 */
export const truncateText = (text: string, max?: number, elipsis = '...') => {
  const shouldTruncate = max && text.length > max;
  return text.substring(0, max) + (shouldTruncate ? elipsis : '');
};

const NAMED_HTML_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
};

/**
 * Decodes HTML character references (numeric and a small set of named
 * entities) to their literal characters. Unknown or malformed references are
 * left untouched. Pure and DOM-free so it is safe on the server.
 */
export const decodeHtmlEntities = (input: string): string => {
  if (typeof input !== 'string') return input;

  return input.replace(
    /&(#\d+|#[xX][0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]*);/g,
    (match, body) => {
      if (body[0] === '#') {
        const isHex = body[1] === 'x' || body[1] === 'X';
        const codePoint = parseInt(
          isHex ? body.slice(2) : body.slice(1),
          isHex ? 16 : 10
        );
        if (Number.isNaN(codePoint)) return match;
        if (codePoint >= 0xd800 && codePoint <= 0xdfff) return match;
        try {
          return String.fromCodePoint(codePoint);
        } catch {
          return match;
        }
      }
      return NAMED_HTML_ENTITIES[body] ?? match;
    }
  );
};
